import React, { useState, useEffect } from 'react';
import { EmailConfig } from '../../types';
import api from '../../services/api';
import './EmailConfigForm.css';

interface EmailConfigFormProps {
  config: EmailConfig | null;
  onClose: () => void;
  onSubmit: () => void;
}

type ConfigType = 'smtp' | 'gmail';

const EmailConfigForm: React.FC<EmailConfigFormProps> = ({ config, onClose, onSubmit }) => {
  const [configType, setConfigType] = useState<ConfigType>('smtp');
  const [formData, setFormData] = useState<any>({
    smtp_host: '',
    smtp_port: '587',
    smtp_username: '',
    smtp_password: '',
    from_email: '',
    to_email: '',
    google_client_id: '',
    google_client_secret: '',
    google_refresh_token: '',
    google_email: '',
    admin_email: '',
    google_redirect_uri: '',
    is_active: true
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (config) {
      const type = config.type || (config.smtp_host ? 'smtp' : 'gmail');
      setConfigType(type);
      setFormData({
        smtp_host: config.smtp_host ?? '',
        smtp_port: config.smtp_port?.toString() ?? '587',
        smtp_username: config.smtp_username ?? '',
        smtp_password: '',
        from_email: config.from_email ?? '',
        to_email: config.to_email ?? '',
        google_client_id: config.google_client_id ?? '',
        google_client_secret: '',
        google_refresh_token: '',
        google_email: config.google_email ?? '',
        admin_email: config.admin_email ?? '',
        google_redirect_uri: config.google_redirect_uri ?? '',
        is_active: config.is_active ?? true
      });
    } else {
      setConfigType('smtp');
      setFormData({
        smtp_host: '',
        smtp_port: '587',
        smtp_username: '',
        smtp_password: '',
        from_email: '',
        to_email: '',
        google_client_id: '',
        google_client_secret: '',
        google_refresh_token: '',
        google_email: '',
        admin_email: '',
        google_redirect_uri: '',
        is_active: true
      });
    }
  }, [config]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setFormData((prev: any) => ({
        ...prev,
        [name]: (e.target as HTMLInputElement).checked
      }));
    } else {
      setFormData((prev: any) => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    let payload: any = { is_active: formData.is_active };

    if (configType === 'smtp') {
      if (!formData.smtp_host || !formData.smtp_port || !formData.smtp_username || !formData.from_email || !formData.to_email) {
        setError('Please fill all SMTP required fields');
        return;
      }
      payload = {
        ...payload,
        smtp_host: formData.smtp_host,
        smtp_port: parseInt(formData.smtp_port),
        smtp_username: formData.smtp_username,
        smtp_password: formData.smtp_password,
        from_email: formData.from_email,
        to_email: formData.to_email
      };
    } else {
      if (!formData.google_client_id || !formData.google_client_secret || !formData.google_refresh_token || !formData.google_email || !formData.admin_email) {
        setError('Please fill all Gmail API required fields');
        return;
      }
      payload = {
        ...payload,
        google_client_id: formData.google_client_id,
        google_client_secret: formData.google_client_secret,
        google_refresh_token: formData.google_refresh_token,
        google_email: formData.google_email,
        admin_email: formData.admin_email,
        google_redirect_uri: formData.google_redirect_uri || null
      };
    }

    try {
      setSaving(true);
      let response;
      if (config) {
        response = await api.put(`/admin/email-config/${config.id}`, payload);
      } else {
        response = await api.post('/admin/email-config', payload);
      }
      if (response.data.success) {
        onSubmit();
      } else {
        setError(response.data.message || 'Failed to save email configuration');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error saving configuration');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{config ? 'Edit Email Configuration' : 'Add Email Configuration'}</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="form-error">{error}</div>}

            <div className="form-group">
              <label>Configuration Type</label>
              <div className="radio-group">
                <label>
                  <input type="radio" value="smtp" checked={configType === 'smtp'} onChange={() => setConfigType('smtp')} />
                  SMTP
                </label>
                <label>
                  <input type="radio" value="gmail" checked={configType === 'gmail'} onChange={() => setConfigType('gmail')} />
                  Gmail API (OAuth2)
                </label>
              </div>
            </div>

            {configType === 'smtp' && (
              <>
                <div className="form-group">
                  <label>SMTP Host *</label>
                  <input type="text" name="smtp_host" value={formData.smtp_host} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>SMTP Port *</label>
                  <select name="smtp_port" value={formData.smtp_port} onChange={handleChange} required>
                    <option value="587">587 (TLS)</option>
                    <option value="465">465 (SSL)</option>
                    <option value="25">25</option>
                    <option value="2525">2525</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Username *</label>
                  <input type="text" name="smtp_username" value={formData.smtp_username} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Password {config && '(leave blank to keep current)'}</label>
                  <input type="password" name="smtp_password" value={formData.smtp_password} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>From Email *</label>
                  <input type="email" name="from_email" value={formData.from_email} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>To Email (Admin) *</label>
                  <input type="email" name="to_email" value={formData.to_email} onChange={handleChange} required />
                </div>
              </>
            )}

            {configType === 'gmail' && (
              <>
                <div className="form-group">
                  <label>Google Client ID *</label>
                  <input type="text" name="google_client_id" value={formData.google_client_id} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Google Client Secret *</label>
                  <input type="text" name="google_client_secret" value={formData.google_client_secret} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Google Refresh Token *</label>
                  <input type="text" name="google_refresh_token" value={formData.google_refresh_token} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Google Email (sender) *</label>
                  <input type="email" name="google_email" value={formData.google_email} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Admin Email (recipient) *</label>
                  <input type="email" name="admin_email" value={formData.admin_email} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Redirect URI (optional)</label>
                  <input type="text" name="google_redirect_uri" value={formData.google_redirect_uri} onChange={handleChange} />
                </div>
              </>
            )}

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input type="checkbox" name="is_active" checked={formData.is_active} onChange={handleChange} />
                <span>Active (this configuration will be used to send emails)</span>
              </label>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="cancel-btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="save-btn" disabled={saving}>
              {saving ? 'Saving...' : config ? 'Update' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmailConfigForm;

// Add this line to satisfy isolatedModules (though not strictly needed)
export {};